import qs from 'qs';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import {
  COMMON_QUERY_PARAMS,
  SUPPORTED_METHODS,
} from '@savgroup-front-common/constants/shared';
import Origin from '@savgroup-front-common/constants/shared/origin';
import { apiCall } from '@savgroup-front-common/core/services';
import { AddressInfoDto, BackResponse } from '@savgroup-front-common/types';

import { CLAIM_GROUP_URL } from './claimGroup.url';

interface GetClaimGroupConfirmationLegacyArgs {
  claimGroupId: string;
}

/**
 * @deprecated use getClaimGroupConfirmation instead
 * @param claimGroupId
 * @returns {Promise<null>}
 */
export async function getClaimGroupConfirmationLegacy({
  claimGroupId,
}: GetClaimGroupConfirmationLegacyArgs) {
  if (!claimGroupId) {
    return null;
  }

  const url = `${APIConfiguration.claim}claimGroups/${claimGroupId}/confirmation`;

  const response = await apiCall<BackResponse<any>>(url, {
    method: SUPPORTED_METHODS.GET,
  });

  return response.value || null;
}

interface SetClaimGroupConfirmationArgs {
  claimGroupId: string;
  phoneNumber: string;
  mail: string;
  swornStatement?: boolean;
  generalSalesCondition?: string;
  issueDate?: string;
  origin?: string;
  ticketId?: string | null;
  invoiceId?: string;
  sourceTokenId?: string;
  storeActorId?: string;
}

export async function setClaimGroupConfirmation({
  claimGroupId,
  phoneNumber,
  mail,
  swornStatement,
  generalSalesCondition,
  issueDate,
  invoiceId,
  sourceTokenId,
  origin = Origin.BACKOFFICE,
  ticketId,
}: SetClaimGroupConfirmationArgs) {
  const isValidPayload = [
    phoneNumber,
    mail,
    swornStatement,
    generalSalesCondition,
    issueDate,
    invoiceId,
    sourceTokenId,
    origin,
    ticketId,
  ].some((item) => item);

  if (!claimGroupId || !isValidPayload) {
    return null;
  }

  const url = CLAIM_GROUP_URL.SET_CLAIM_GROUP_CONFIRMATION_COMMAND.replace(
    COMMON_QUERY_PARAMS.CLAIM_GROUP_ID,
    claimGroupId,
  );

  const response = await apiCall<BackResponse<string>>(url, {
    method: SUPPORTED_METHODS.PUT,
    json: {
      phoneNumber,
      mail,
      swornStatement,
      generalSalesCondition,
      issueDate,
      invoiceId,
      sourceTokenId,
      origin,
      ticketId,
    },
  });

  return response.value || null;
}

interface GetClaimGroupCarrierLegacyArgs {
  claimGroupId: string;
  mode: string;
}

/**
 * @deprecated use getClaimGroupCarrier instead
 * @param claimGroupId
 * @param mode
 * @returns {Promise<null>}
 */
export async function getClaimGroupCarrierLegacy({
  claimGroupId,
  mode,
}: GetClaimGroupCarrierLegacyArgs) {
  if (!claimGroupId || !mode) {
    return null;
  }

  const url = CLAIM_GROUP_URL.GET_CLAIM_GROUP_CARRIER.replace(
    COMMON_QUERY_PARAMS.CLAIM_GROUP_ID,
    claimGroupId,
  );

  const builtUrl = `${url}?${qs.stringify({ mode })}`;

  const response = await apiCall<BackResponse<any>>(builtUrl, {
    method: SUPPORTED_METHODS.GET,
  });

  return response.value || null;
}

interface GetClaimGroupHandlingLegacyArgs {
  claimGroupId: string;
}

/**
 * @deprecated use getClaimGroupHandling instead
 * @param claimGroupId
 * @returns {Promise<{}|null>}
 */
export async function getClaimGroupHandlingLegacy({
  claimGroupId,
}: GetClaimGroupHandlingLegacyArgs) {
  if (!claimGroupId) {
    return null;
  }

  const url = CLAIM_GROUP_URL.GET_CLAIM_GROUP_HANDLING.replace(
    COMMON_QUERY_PARAMS.CLAIM_GROUP_ID,
    claimGroupId,
  );

  const response = await apiCall<BackResponse<any>>(url, {
    method: SUPPORTED_METHODS.GET,
  });

  return response.value || {};
}

interface SetClaimGroupHandlingArgs {
  claimGroupId: string;
  payload: {
    handlingMode: string;
    pickupPointId?: string;
    pickupPointAddress?: string;
    carrierCustomerPriceId: string;
    carrierSellerPriceId: string;
    address?: AddressInfoDto;
  };
}

export async function setClaimGroupHandling({
  claimGroupId,
  payload,
}: SetClaimGroupHandlingArgs) {
  if (!claimGroupId || !payload) {
    return null;
  }

  const {
    handlingMode,
    pickupPointId,
    pickupPointAddress,
    carrierCustomerPriceId,
    carrierSellerPriceId,
    address,
  } = payload;

  const url = CLAIM_GROUP_URL.SET_CLAIM_GROUP_HANDLING.replace(
    COMMON_QUERY_PARAMS.CLAIM_GROUP_ID,
    claimGroupId,
  );

  const response = await apiCall<BackResponse<any>>(url, {
    method: SUPPORTED_METHODS.POST,
    json: {
      handlingMode,
      pickupPointId,
      PickupPointAddress: pickupPointAddress,
      carrierCustomerPriceId,
      carrierSellerPriceId,
      address,
    },
  });

  return response.value || {};
}

interface GetClaimGroupClaimIdsArgs {
  claimGroupId: string;
}

export async function getClaimGroupClaimIds({
  claimGroupId,
}: GetClaimGroupClaimIdsArgs) {
  if (!claimGroupId) {
    return [];
  }
  const url = CLAIM_GROUP_URL.GET_CLAIM_GROUP_CLAIM_IDS.replace(
    COMMON_QUERY_PARAMS.CLAIM_GROUP_ID,
    claimGroupId,
  );

  const response = await apiCall<BackResponse<any>>(url, {
    method: SUPPORTED_METHODS.GET,
  });

  if (response.failure) {
    throw new Error(`Got an exception trying to getClaimGroupClaimIds`);
  }

  return response.value || {};
}

interface GetClaimGroupClaimSummariesLegacyArgs {
  claimGroupId: string;
}

/**
 * @deprecated use getClaimGroupClaimSummaries instead
 * @param claimGroupId
 * @returns {Promise<*[]>}
 */
export async function getClaimGroupClaimSummariesLegacy({
  claimGroupId,
}: GetClaimGroupClaimSummariesLegacyArgs) {
  if (!claimGroupId) {
    return [];
  }
  const url = CLAIM_GROUP_URL.GET_CLAIM_GROUP_CLAIMS_SUMMARY.replace(
    COMMON_QUERY_PARAMS.CLAIM_GROUP_ID,
    claimGroupId,
  );

  const response = await apiCall<BackResponse<any>>(url, {
    method: SUPPORTED_METHODS.GET,
  });

  if (response.failure) {
    throw new Error(`Got an exception trying to getClaimGroupClaimSummaries`);
  }

  return response.value || [];
}
